import { FormEvent, useMemo, useState } from 'react';
import { TbCalendar, TbCode, TbPhone, TbUser } from 'react-icons/tb';
import useCountdown from '../../../hooks/use-countdown';
import { sendPhoneCode, updateMe, validatePhoneNumber, verifyPhoneCode } from '../../../core/auth';
import { showError, showSuccess } from '../../../core/utils';
import { IProduct, IUser } from '../../../type';
import BirthdayInput, { isUnderageBirthday } from '../birthday-input';

export default function VerifyPhoneGroup({
  user,
  product,
  onFinish,
  apiEndpoint,
  onError,
  accessToken = '',
  onClickClose,
}: {
  user: IUser;
  product: IProduct;
  onFinish: (data: { phoneNumber: string; name: string; birthdayDate: string }) => void;
  onError: Function;
  apiEndpoint: string;
  accessToken: string;
  /** Optional callback khi user click ✕ góc trên phải — App.tsx quản lý state openPhone + lưu dismiss flag. */
  onClickClose?: () => void;
}) {
  const OTP_COOLDOWN = 300; // 5 phút — khớp backend
  const { countTime, setCountdown } = useCountdown(OTP_COOLDOWN);
  const [phoneValue, setPhoneValue] = useState(user.phoneNumber || '');
  const [isUnderage, setIsUnderage] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const phoneDigits = phoneValue.replace(/\D/g, '');
  const phoneValid = useMemo(() => validatePhoneNumber(phoneValue.trim()), [phoneValue]);
  const coolingDown = countTime < OTP_COOLDOWN;
  const canSendOtp = phoneValid && !coolingDown;
  /** Đủ 10 số mà sai đầu số / format → hiện lỗi dưới ô */
  const phoneError = phoneDigits.length >= 10 && !phoneValid
    ? 'Số điện thoại không hợp lệ (phải là SĐT di động Việt Nam 10 số)'
    : '';

  return (
    <form
      className={"verify_phone"}
      onSubmit={async (event: FormEvent<HTMLFormElement>) => {
        try {
          event.preventDefault();
          const formData = new FormData(event.currentTarget);
          const name = (formData.get('name') as string) || '';
          const birthdayDate = (formData.get('birthdayDate') as string) || '';
          const phoneNumber = phoneValue.trim();
          const code = formData.get('code') as string;

          if (!validatePhoneNumber(phoneNumber)) {
            showError('Số điện thoại không hợp lệ, hãy kiểm tra lại');
            return;
          }

          if (isUnderage || isUnderageBirthday(birthdayDate)) {
            showError('Bạn chưa đủ 18 tuổi để tham gia trò chơi');
            return;
          }

          setSubmitting(true);
          await verifyPhoneCode(
            { phoneNumber, code },
            apiEndpoint,
            accessToken
          );
          await updateMe(
            { name, birthdayDate },
            apiEndpoint,
            accessToken
          );
          showSuccess('Số điện thoại của bạn đã được xác thực thành công');
          onFinish({ phoneNumber, name: name.trim(), birthdayDate });
        } catch (e: any) {
          showError(e?.response?.data || e);
          onError(e?.response?.data || e);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {!product.requirePhoneWebActive && onClickClose && <button type="button" className='close-btn' onClick={onClickClose}>
        ×
      </button>}
      <h1 style={{ textAlign: 'center' }}>Xác thực số điện thoại</h1>
      <div className="form_item">
        <TbUser />
        <input
          id="name"
          name="name"
          defaultValue={user.name || ''}
          placeholder="Họ và tên"
          required
          minLength={3}
          maxLength={50}
        />
      </div>
      <BirthdayInput
        icon={<TbCalendar />}
        defaultValue={user.birthdayDate}
        onUnderageChange={setIsUnderage}
      />
      <div className={`phone_field${phoneError ? ' has_error' : ''}`}>
        <div className="form_item">
          <TbPhone />
          <input
            id="phoneNumber"
            name="phoneNumber"
            value={phoneValue}
            inputMode="numeric"
            maxLength={10}
            onChange={(e) => {
              const next = e.target.value.replace(/\D/g, '').slice(0, 10);
              setPhoneValue(next);
            }}
            placeholder="Nhập số điện thoại"
            required
          />
          <button
            className={"input_btn"}
            type="button"
            disabled={!canSendOtp}
            onClick={async () => {
              try {
                if (!phoneValid) return;
                const resp = await sendPhoneCode({ phoneNumber: phoneValue.trim() }, apiEndpoint, accessToken);
                if (resp.data.isPhoneNumberVerified) {
                  showError(resp.data.message);
                  return;
                }
                showSuccess('Một mã xác thực đã được gửi về số điện thoại của ban, xin hãy kiểm tra.');
                setCountdown(OTP_COOLDOWN);
              } catch (e: any) {
                showError(e?.response?.data || e);
                onError(e?.response?.data || e);
              }
            }}
          >
            Lấy mã
            {' '}
            {coolingDown && `(${countTime}s)`}
          </button>
        </div>
        {phoneError ? <p className="field_error">{phoneError}</p> : null}
      </div>
      <div className="form_item">
        <TbCode />
        <input id="code" name="code" placeholder="Nhập mã xác minh" required />
      </div>
      <button
        className={'primary_btn'}
        type="submit"
        disabled={submitting || isUnderage || !phoneValid}
      >
        Xác thực
      </button>
    </form>
  );
}
